import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ServicesSection() {
  const { t } = useTranslation();

  const services = [
    {
      icon: 'ri-tv-2-line',
      number: t('services.s1.number'),
      title: t('services.s1.title'),
      subtitle: t('services.s1.subtitle'),
      color: '#00ff41',
      description: t('services.s1.description'),
      features: [
        t('services.s1.f1'),
        t('services.s1.f2'),
        t('services.s1.f3'),
      ],
      href: '/services/tvc'
    },
    {
      icon: 'ri-music-2-line',
      number: t('services.s2.number'),
      title: t('services.s2.title'),
      subtitle: t('services.s2.subtitle'),
      color: '#00d9ff',
      description: t('services.s2.description'),
      features: [
        t('services.s2.f1'),
        t('services.s2.f2'),
        t('services.s2.f3'),
      ],
      href: '/services/music-mv'
    },
    {
      icon: 'ri-robot-2-line',
      number: t('services.s3.number'),
      title: t('services.s3.title'),
      subtitle: t('services.s3.subtitle'),
      color: '#ff006e',
      description: t('services.s3.description'),
      features: [
        t('services.s3.f1'),
        t('services.s3.f2'),
        t('services.s3.f3'),
      ],
      href: '/services/digital-human'
    },
    {
      icon: 'ri-slideshow-line',
      number: t('services.s4.number'),
      title: t('services.s4.title'),
      subtitle: t('services.s4.subtitle'),
      color: '#9d4edd',
      description: t('services.s4.description'),
      features: [
        t('services.s4.f1'),
        t('services.s4.f2'),
        t('services.s4.f3'),
      ],
      href: '/services/exhibition'
    }
  ];

  return (
    <section id="services" className="relative py-24 bg-gradient-to-b from-[#16213e] to-[#0a0118] overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-[#00ff41]"
            style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 53) % 100}%`,
              animation: `twinkle ${2 + (i % 3)}s ease-in-out infinite`,
              animationDelay: `${(i % 5) * 0.4}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-[#00ff41]/10 border border-[#00ff41] text-[#00ff41] text-xs font-['JetBrains_Mono'] font-semibold mb-6"
            style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
          >
            {t('services.sectionTag')}
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white font-['Space_Grotesk'] mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff41] via-[#00d9ff] to-[#ff006e]">
              {t('services.title')}
            </span>
          </h2>
          <p className="text-gray-400 font-['JetBrains_Mono'] text-sm">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <Link
              key={index}
              to={service.href}
              className="group relative bg-[#1a0b2e]/30 backdrop-blur-sm border-2 border-gray-700/50 hover:border-opacity-100 p-8 transition-all duration-500 hover:scale-105 hover:shadow-2xl block cursor-pointer"
              style={{
                clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))',
                borderColor: service.color
              }}
            >
              <div className="mb-6 flex items-center justify-center w-20 h-20 relative">
                <div className="absolute inset-0 opacity-20 blur-xl" style={{ backgroundColor: service.color }}></div>
                <i className={`${service.icon} text-5xl relative z-10`} style={{ color: service.color }}></i>
              </div>

              <div className="mb-4">
                <div className="text-xs font-['JetBrains_Mono'] font-bold mb-2" style={{ color: service.color }}>
                  {service.number}
                </div>
                <h3 className="text-2xl font-bold text-white font-['Space_Grotesk'] mb-2">{service.title}</h3>
                <p className="text-sm font-['JetBrains_Mono']" style={{ color: service.color }}>{service.subtitle}</p>
              </div>

              <p className="text-gray-300 text-sm mb-6 leading-relaxed">{service.description}</p>

              <div className="space-y-3 mb-8">
                <div className="text-xs font-['JetBrains_Mono'] font-bold mb-3" style={{ color: service.color }}>
                  {t('services.featuresLabel')}
                </div>
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-lg mt-0.5 flex-shrink-0" style={{ color: service.color }}>▸</span>
                    <span className="text-gray-400 text-sm leading-relaxed">{feature}</span>
                  </div>
                ))}
              </div>

              <div
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-['JetBrains_Mono'] font-semibold border transition-all duration-300 whitespace-nowrap"
                style={{
                  borderColor: service.color,
                  color: service.color,
                  background: `${service.color}10`,
                  clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
                }}
              >
                <span>{t('services.cta')}</span>
                <i className="ri-arrow-right-line"></i>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800">
                <div className="h-full transition-all duration-500 group-hover:w-full w-0" style={{ backgroundColor: service.color }}></div>
              </div>

              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at center, ${service.color}, transparent)`,
                  clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))'
                }}
              ></div>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes twinkle { 0%, 100% { opacity: 0.2; transform: scale(1); } 50% { opacity: 1; transform: scale(1.5); } }
      `}</style>
    </section>
  );
}
